import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { translate as t } from '@fiverr-private/i18n-react';
import { logger, stats } from '@fiverr-private/obs';
import { useRealtimeSubscription } from '../../../../utils/realtime/useRealtimeSubscription';
import { useImportLinkedInData } from '../../../../hooks/useImportWorkExperience';
import RealtimeListener from '../../../../utils/realtime/RealtimeListener';
import { WorkExperience } from '../../../../gql-operations/gql-generated';
import { getLinkedInData } from '../../../../apis/workExperience';
import { CurrentViewState, LinkedInImportTriggerResponse, WorkExperienceViewStates } from '../../shared/types';
import { IMPORT_TIMEOUT, LinkedInImportStatusEvent, T_KEY } from '../../utils';
import { parseImportedWorkExperiences } from './utils';
import { ImportState } from './index';

export interface UseImportWorkExperienceParams {
    pushkinClient: typeof RealtimeListener;
    setCurrentWorkExperiences: (workExperiences: WorkExperience[] | undefined | null) => void;
    setCurrentViewState: Dispatch<SetStateAction<CurrentViewState>>;
    setError: UseFormSetError<{ profileUrl?: string }>;
}

const metric = 'packages.seller_pages_toolbox.import';

const useImportingLogic = ({
    pushkinClient,
    setCurrentWorkExperiences,
    setCurrentViewState,
    setError,
}: UseImportWorkExperienceParams) => {
    const { subscribe, unsubscribe } = useRealtimeSubscription(pushkinClient);
    const [importState, setImportState] = useState<ImportState>(ImportState.Initial);
    const currentSubscriptionRef = useRef<{ timeoutHandle?: NodeJS.Timeout; channelName: string }>();

    const cleanupWebsocketAndChangeState = useCallback(
        (state?: ImportState) => {
            const { channelName, timeoutHandle } = currentSubscriptionRef.current || {};

            if (state !== undefined) {
                setImportState(state);
            }

            if (channelName) {
                unsubscribe(channelName);
            }

            clearTimeout(timeoutHandle);

            currentSubscriptionRef.current = undefined;
        },
        [unsubscribe]
    );

    useEffect(
        () => () => {
            cleanupWebsocketAndChangeState();
        },
        [cleanupWebsocketAndChangeState]
    );

    const { isLoading: isTriggerLinkedInLoading, mutate: triggerLinkedInImport } = useImportLinkedInData({
        onSuccess: async (importTriggerResponse: LinkedInImportTriggerResponse) => {
            if (!importTriggerResponse?.channelName) {
                logger.error('Trigger import linkedin profile failed');
                stats.count(metric, 'trigger_import_failed');
                setImportState(ImportState.Error);
                return;
            }

            if (currentSubscriptionRef.current) {
                cleanupWebsocketAndChangeState();
            }

            const channelName = importTriggerResponse?.channelName;
            setImportState(ImportState.Loading);

            currentSubscriptionRef.current = {
                timeoutHandle: setTimeout(() => {
                    cleanupWebsocketAndChangeState(ImportState.Error);
                    logger.error('Import linkedin profile failed - Timeout');
                    stats.count(metric, 'profile_fetch_timeout');
                }, IMPORT_TIMEOUT),
                channelName,
            };

            await subscribe(channelName, {
                [LinkedInImportStatusEvent.PROFILE_FETCH_SUCCESS]: async () => {
                    stats.count(metric, 'profile_fetch_success');
                    logger.info(`Import linkedin profile success - ${LinkedInImportStatusEvent.PROFILE_FETCH_SUCCESS}`);
                    const importedData = await getLinkedInData();
                    const importedWorkExperiences = parseImportedWorkExperiences(importedData?.workExperiences);
                    setCurrentWorkExperiences(importedWorkExperiences);
                    setCurrentViewState((currentViewState) => ({
                        ...currentViewState,
                        viewState: WorkExperienceViewStates.List,
                        isDirty: true,
                    }));
                    cleanupWebsocketAndChangeState(ImportState.Done);
                },
                [LinkedInImportStatusEvent.PROFILE_FETCH_FAILED]: () => {
                    stats.count(metric, 'profile_fetch_failed');
                    logger.error(`Import linkedin profile failed - ${LinkedInImportStatusEvent.PROFILE_FETCH_FAILED}`);
                    cleanupWebsocketAndChangeState(ImportState.Error);
                },
                [LinkedInImportStatusEvent.PROFILE_NOT_FOUND]: () => {
                    stats.count(metric, 'profile_not_found');
                    logger.warn(`Import linkedin profile failed - ${LinkedInImportStatusEvent.PROFILE_NOT_FOUND}`);
                    setError('profileUrl', { message: t(`${T_KEY}.import.not_found_error`) });
                    cleanupWebsocketAndChangeState(ImportState.Initial);
                },
            });
        },
        onError: () => {
            setImportState(ImportState.Error);
        },
    });

    return {
        isTriggerLinkedInLoading,
        triggerLinkedInImport,
        importState,
    };
};

export default useImportingLogic;
